import { useState, useMemo, useEffect } from "react"
import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import {
  COMMISSION_DEFAULTS,
  UK_AVERAGE_FEE,
  calculateCommission,
  formatCurrency,
} from "@/data/tools/commission-defaults"
import { CalculatorResultCard, CalculatorSlider, CalculatorActionsBar } from "@/components/public/calculators"
import { encodeCalculatorParams, decodeCalculatorParams } from "@/lib/calculator-url-params"
import { generateCalculatorPdf } from "@/lib/calculator-pdf"

/* ------------------------------------------------------------------ */
/* FAQ data                                                            */
/* ------------------------------------------------------------------ */

const FAQ = [
  {
    q: "What is the average estate agent fee in the UK?",
    a: "The average UK estate agent fee is approximately 1.18% + VAT for a sole agency agreement. Multi-agency agreements (where you instruct more than one agent) typically charge 2.5\u20133% + VAT. Fees vary significantly by region and the level of service provided.",
  },
  {
    q: "Do estate agents charge VAT on their fees?",
    a: "Yes. All VAT-registered estate agents charge 20% VAT on top of their commission. Most agents are VAT-registered. This means a 1.2% fee is effectively 1.44% including VAT. Always confirm whether a quoted fee includes or excludes VAT.",
  },
  {
    q: "Can I negotiate estate agent fees?",
    a: "Yes, estate agent fees are always negotiable. However, the cheapest fee doesn't always mean the best deal. An agent who charges 1.5% but achieves a sale price \u00A320,000 higher than a 0.75% agent will earn you more money overall. Focus on the agent's marketing quality and local track record.",
  },
  {
    q: "How can agents justify higher fees?",
    a: "Agents who invest in superior marketing (professional photography, video tours, targeted social media campaigns) can justify higher fees by demonstrating better results. Agents using video marketing consistently report winning more instructions at higher fee rates because vendors see the value in premium marketing.",
  },
  {
    q: "What is the difference between sole agency and multi-agency fees?",
    a: "Sole agency means you instruct one agent exclusively, typically at 1\u20131.5% + VAT. Multi-agency means you instruct two or more agents simultaneously, with only the one who sells earning the fee, usually 2.5\u20133% + VAT. Sole agency is cheaper but limits your market exposure.",
  },
  {
    q: "When do I pay estate agent commission?",
    a: "Estate agent commission is paid on completion, not on exchange. Your solicitor deducts the agent's fee from the sale proceeds before transferring the remainder to you. You don't need to pay anything upfront unless your agent charges separate marketing fees.",
  },
  {
    q: "Are online estate agents cheaper than high street agents?",
    a: "Online agents typically charge a fixed fee (\u00A3500\u2013\u00A31,500) rather than a percentage. This can be cheaper on expensive properties but works out similar or more expensive on lower-value homes. The key difference is service level: high street agents usually handle viewings and negotiations, while some online agents leave this to you.",
  },
  {
    q: "What is a no-sale-no-fee agreement?",
    a: "Most traditional estate agents work on a no-sale-no-fee basis, meaning you only pay commission if they successfully sell your property. Some online agents charge upfront fees regardless of whether the property sells. Always check the terms before signing.",
  },
  {
    q: "How does commission work with new build developments?",
    a: "For new builds, the developer typically pays the agent's fee, not the buyer. Estate agents introducing buyers to new developments usually earn 1\u20133% from the developer. This is why some agents are keen to promote new build sites alongside their existing stock.",
  },
  {
    q: "Can I sell my house without an estate agent?",
    a: "Yes, private sales are legal and save you the commission. However, you'll need to handle marketing, viewings, negotiations, and compliance yourself. Estate agents typically achieve higher sale prices than private sellers, and the net proceeds (after commission) are often higher than a private sale would achieve.",
  },
]

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function CommissionCalculatorPage({
  meta,
}: {
  meta: SeoMeta
}) {
  const initialParams = useMemo(() => {
    if (typeof window === "undefined") return COMMISSION_DEFAULTS
    const sp = new URLSearchParams(window.location.search)
    return decodeCalculatorParams(
      {
        salePrice: COMMISSION_DEFAULTS.salePrice,
        feePercent: COMMISSION_DEFAULTS.feePercent,
        includeVat: COMMISSION_DEFAULTS.includeVat,
        propertiesPerMonth: COMMISSION_DEFAULTS.propertiesPerMonth,
      },
      sp,
    )
  }, [])

  const [salePrice, setSalePrice] = useState<number>(initialParams.salePrice as number)
  const [feePercent, setFeePercent] = useState<number>(initialParams.feePercent as number)
  const [includeVat, setIncludeVat] = useState<boolean>(initialParams.includeVat as boolean)
  const [propertiesPerMonth, setPropertiesPerMonth] = useState<number>(initialParams.propertiesPerMonth as number)

  const result = calculateCommission({
    salePrice,
    feePercent,
    includeVat,
    propertiesPerMonth,
  })

  useEffect(() => {
    const params = encodeCalculatorParams({ salePrice, feePercent, includeVat, propertiesPerMonth })
    window.history.replaceState(null, "", `?${params}`)
  }, [salePrice, feePercent, includeVat, propertiesPerMonth])

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}?${encodeCalculatorParams({ salePrice, feePercent, includeVat, propertiesPerMonth })}`
      : ""
  const shareTitle = `Estate agent commission: ${formatCurrency(includeVat ? result.feeIncVat : result.feeExVat)} per sale at ${feePercent.toFixed(1)}% on ${formatCurrency(salePrice)}`

  const handleDownloadPdf = () =>
    generateCalculatorPdf({
      title: "Commission Calculation",
      subtitle: `Estate agent fees at ${feePercent.toFixed(1)}% ${includeVat ? "inc." : "exc."} VAT`,
      sections: [
        {
          title: "Results",
          rows: [
            { label: "Fee per sale", value: formatCurrency(includeVat ? result.feeIncVat : result.feeExVat), sub: `${feePercent.toFixed(1)}% ${includeVat ? "inc." : "exc."} VAT on ${formatCurrency(salePrice)}`, accent: true },
            { label: "Monthly revenue", value: formatCurrency(result.monthlyRevenue), sub: `${propertiesPerMonth} sale${propertiesPerMonth === 1 ? "" : "s"} per month` },
            { label: "Annual revenue", value: formatCurrency(result.annualRevenue), sub: `${propertiesPerMonth * 12} sales per year` },
          ],
        },
      ],
      url: shareUrl,
      generatedAt: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }),
    })

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Estate Agent Commission Calculator",
            description: meta.description,
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" },
            provider: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://quiteright.co.uk",
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Tools", path: "/tools" }, { name: "Commission Calculator" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Calculator
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Estate agent commission calculator
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Calculate your commission per sale, monthly revenue, and see what
            a small fee increase could mean for your annual income.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col gap-10 lg:flex-row lg:gap-16">
            {/* Input panel */}
            <div className="flex-1 space-y-8">
              <div>
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Your numbers<span className="text-red">.</span>
                </h2>
                <p className="mt-1 text-sm text-slate">
                  Adjust to match your agency. UK average is {UK_AVERAGE_FEE}% exc. VAT.
                </p>
              </div>

              <CalculatorSlider
                label="Average sale price"
                value={salePrice}
                min={100_000}
                max={2_000_000}
                step={10_000}
                formatValue={(v) => formatCurrency(v)}
                onChange={setSalePrice}
              />

              <CalculatorSlider
                label="Fee percentage (exc. VAT)"
                value={feePercent}
                min={0.5}
                max={3.5}
                step={0.1}
                formatValue={(v) => `${v.toFixed(1)}%`}
                onChange={setFeePercent}
              />

              <CalculatorSlider
                label="Sales per month"
                value={propertiesPerMonth}
                min={1}
                max={20}
                step={1}
                formatValue={(v) => `${v}`}
                onChange={setPropertiesPerMonth}
              />

              {/* VAT toggle */}
              <div>
                <label className="text-sm font-medium text-charcoal">
                  Show figures
                </label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {[
                    { value: true, label: "Including VAT", desc: "What the vendor pays" },
                    { value: false, label: "Excluding VAT", desc: "Your net revenue" },
                  ].map((option) => (
                    <button
                      key={option.label}
                      type="button"
                      onClick={() => setIncludeVat(option.value)}
                      className={`rounded-lg border px-3 py-3 text-left transition-all ${
                        includeVat === option.value
                          ? "border-red bg-red/5 ring-1 ring-red/20"
                          : "border-stone bg-white hover:border-stone/80"
                      }`}
                    >
                      <p
                        className={`text-sm font-semibold ${
                          includeVat === option.value ? "text-red" : "text-charcoal"
                        }`}
                      >
                        {option.label}
                      </p>
                      <p className="mt-0.5 text-xs text-warm-grey">{option.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results panel */}
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Your revenue<span className="text-red">.</span>
                </h2>
                <p className="mt-1 text-sm text-slate">
                  {includeVat ? "Figures include 20% VAT." : "Figures exclude VAT."}
                </p>
              </div>

              <CalculatorActionsBar shareUrl={shareUrl} shareTitle={shareTitle} onDownloadPdf={handleDownloadPdf} />

              <CalculatorResultCard
                label="Fee per sale"
                value={formatCurrency(includeVat ? result.feeIncVat : result.feeExVat)}
                sub={`${feePercent.toFixed(1)}% ${includeVat ? "inc." : "exc."} VAT on ${formatCurrency(salePrice)}`}
                accent
                large
              />

              <div className="grid grid-cols-2 gap-4">
                <CalculatorResultCard
                  label="Monthly revenue"
                  value={formatCurrency(result.monthlyRevenue)}
                  sub={`${propertiesPerMonth} sale${propertiesPerMonth === 1 ? "" : "s"} per month`}
                />
                <CalculatorResultCard
                  label="Annual revenue"
                  value={formatCurrency(result.annualRevenue)}
                  sub={`${propertiesPerMonth * 12} sales per year`}
                />
              </div>

              {/* +0.1% comparison */}
              <div className="rounded-xl border border-red/20 bg-red/[0.03] p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-red">
                  What if you charged {result.comparisonFeePercent.toFixed(1)}% instead?
                </p>
                <p className="mt-2 text-sm text-slate">
                  A {feePercent.toFixed(1)}% → {result.comparisonFeePercent.toFixed(1)}%
                  increase adds{" "}
                  <span className="font-bold text-red">
                    +{formatCurrency(result.annualDifference)}
                  </span>{" "}
                  to your annual revenue. That&rsquo;s{" "}
                  {formatCurrency(Math.round(result.annualDifference / 12))}/month extra.
                </p>
                <p className="mt-2 text-xs text-warm-grey">
                  Agents who invest in video marketing can justify the difference.
                </p>
              </div>

              {includeVat && (
                <div className="rounded-xl border border-stone bg-white p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-charcoal">
                    VAT breakdown
                  </p>
                  <div className="mt-2 space-y-1 text-sm text-slate">
                    <div className="flex justify-between">
                      <span>Fee exc. VAT</span>
                      <span className="font-medium text-charcoal">
                        {formatCurrency(result.feeExVat)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>VAT (20%)</span>
                      <span className="font-medium text-charcoal">
                        {formatCurrency(result.vatAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-stone pt-1">
                      <span className="font-medium text-charcoal">Total inc. VAT</span>
                      <span className="font-bold text-charcoal">
                        {formatCurrency(result.feeIncVat)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            How agent fees work<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Estate agent fees are typically charged as a percentage of the
              final sale price, plus VAT. The fee is only payable on
              completion; if the property doesn&rsquo;t sell, you don&rsquo;t
              pay.
            </p>
            <p>
              Sole agency agreements (one agent) usually charge 1.0-1.5%,
              while multi-agency agreements (multiple agents) charge 2.5-3.0%.
              The agent who secures the buyer earns the full fee in a
              multi-agency arrangement.
            </p>
            <p>
              The most successful agents don&rsquo;t compete on price. They
              compete on marketing quality and results. Agents who use
              professional video alongside photography consistently win more
              instructions and achieve better outcomes for vendors.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "1.18%", label: "average UK estate agent fee (exc. VAT)" },
            { stat: "40%", label: "of vendors say marketing influenced their agent choice" },
            { stat: "73%", label: "of vendors prefer agents offering video" },
            { stat: "+0.1%", label: "fee increase can add thousands per year" },
          ].map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">{item.label}</p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related tools */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related tools<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Link
              href="/tools/instruction-calculator"
              className="group rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/20 hover:shadow-sm"
            >
              <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                Instruction calculator
              </p>
              <p className="mt-1 text-sm text-slate">
                See how video marketing pays for itself in extra instructions.
              </p>
            </Link>
            <Link
              href="/tools/marketing-roi-calculator"
              className="group rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/20 hover:shadow-sm"
            >
              <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                Marketing ROI calculator
              </p>
              <p className="mt-1 text-sm text-slate">
                Compare cost-per-lead across your marketing channels.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Win more instructions at higher fees
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Agents who show a professional video at market appraisals win the
            instruction more often, and vendors are happy to pay more for
            premium marketing.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-8 py-4 text-lg font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
          <p className="mt-5 text-sm text-warm-grey">
            14-day free trial &middot; No credit card &middot; Cancel any time
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
